import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { Task } from '../../types/task';
import { taskService } from '../../services/taskService';
import { bidService } from '../../services/bidService';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { formatCurrency } from '../../utils/currency';
import PageBackButton from '../../components/PageBackButton';

interface CreateBidFormData {
  amount: number;
  description: string;
}

const CreateBid: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { taskId } = useParams<{ taskId: string }>();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<CreateBidFormData>();

  useEffect(() => {
    const fetchTask = async () => {
      try {
        if (!taskId) return;
        const taskData = await taskService.getTaskById(parseInt(taskId));
        setTask(taskData);
      } catch (err: any) {
        setError(err.response?.data?.detail || t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchTask();
  }, [taskId, t]);

  const getLocale = () => getDateFnsLocale(i18n.language);

  const onSubmit = async (data: CreateBidFormData) => {
    if (!taskId) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      await bidService.createBid({
        ...data,
        task_id: parseInt(taskId)
      });
      
      navigate('/bids/my');
    } catch (err: any) {
      setError(err.response?.data?.detail || t('bids.create.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-8 text-center">
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <PageBackButton />
        </Box>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-8">
        <PageBackButton />
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!task) {
    return (
      <Container className="py-8">
        <PageBackButton />
        <Alert severity="error">{t('tasks.not_found')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" className="py-8">
      <PageBackButton />
      <Typography variant="h4" component="h1" className="mb-6">
        {t('bids.create.title')}
      </Typography>

      <Card className="mb-6">
        <CardContent>
          <Typography variant="h6" className="mb-2">
            {t('bids.create.task_details')}
          </Typography>
          <Divider className="mb-4" />
          
          <Typography variant="h5" className="mb-2">
            {task.title}
          </Typography>
          <Typography color="textSecondary" className="mb-2">
            {task.description}
          </Typography>
          <Typography className="mb-2">
            {t('tasks.list.card.budget')}: {formatCurrency(task.budget, i18n.language, task.currency)}
          </Typography>
          <Typography>
            {t('tasks.list.card.deadline')}:{' '}
            {format(new Date(task.deadline), 'Pp', { locale: getLocale() })}
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <TextField
              fullWidth
              label={t('bids.create.form.amount')}
              type="number"
              error={!!errors.amount}
              helperText={errors.amount?.message}
              {...register('amount', {
                required: t('bids.create.form.amount_required'),
                min: {
                  value: 1,
                  message: t('bids.create.form.amount_min')
                }
              })}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label={t('bids.create.form.description')}
              error={!!errors.description}
              helperText={errors.description?.message}
              placeholder={t('bids.create.form.description_placeholder')}
              {...register('description', {
                required: t('bids.create.form.description_required'),
                minLength: {
                  value: 20,
                  message: t('bids.create.form.description_min_length')
                }
              })}
            />

            {error && (
              <Alert severity="error" className="mt-4">
                {error}
              </Alert>
            )}

            <Box className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                disabled={submitting}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={submitting}
              >
                {submitting ? t('common.loading') : t('common.submit')}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CreateBid; 